import { humanize, shortDate } from '../../lib/formatLabels';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStudentEngagement } from '../../api/hooks';
import { SchoolApi } from '../../api/client';
import { EmptyState, Track, Spinner, ErrorPanel } from '../../components/ui';
import { FaChartLine } from '../../components/ui/icons';
import ColumnBars from '../../components/charts/ColumnBars';
import Box from './components/Box';
import MiniStat from './components/MiniStat';
import { possessive } from './lib/format';

interface Props {
  id: number;
  /** The student's class, for the "class 610" caption under the 30-day study time. */
  cohort?: { gradeId: number | null; sectionId: number | null };
  gender?: string | null;
}

type EngagementData = NonNullable<ReturnType<typeof useStudentEngagement>['data']>;

export default function EngagementTab({ id, cohort, gender }: Props) {
  const { data, isLoading, isError } = useStudentEngagement(id);
  const gradeId = cohort?.gradeId ?? null;
  const classQ = useQuery({
    queryKey: ['school', 'student360', 'class-engagement', gradeId, cohort?.sectionId ?? null],
    queryFn: () => SchoolApi.analyticsEngagement({ grade: gradeId as number, ...(cohort?.sectionId != null ? { section: cohort.sectionId } : {}) }),
    enabled: gradeId != null,
  });
  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel />;
  return <EngagementContent data={data} classMinutes30d={classQ.data?.avg_study_minutes_30d ?? null} gender={gender} />;
}

function EngagementContent({ data, classMinutes30d, gender }: { data: EngagementData; classMinutes30d: number | null; gender?: string | null }) {
  const k = data.kpis;
  const series = data.daily_minutes_series;

  const buckets = useMemo(
    () => series.map((r) => ({ label: '', value: r.minutes, highlight: r.minutes === 0, title: `${shortDate(r.date)}: ${r.minutes} min` })),
    [series],
  );
  const axis = useMemo(() => {
    if (series.length === 0) return [];
    const idx = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * (series.length - 1)));
    return Array.from(new Set(idx)).map((i) => shortDate(series[i].date));
  }, [series]);
  const baseline = useMemo(() => {
    const withBase = series.filter((r) => r.personal_baseline !== null);
    return withBase.length ? withBase[withBase.length - 1].personal_baseline : null;
  }, [series]);

  const activeDays = useMemo(() => series.filter((r) => r.minutes > 0).length, [series]);
  const chartMax = Math.max(1, ...series.map((r) => r.minutes));
  const CHART_H = 132;

  const usageEntries = useMemo(
    () => Object.entries(data.feature_usage).sort((a, b) => b[1] - a[1]).slice(0, 6),
    [data.feature_usage],
  );
  const usageMax = Math.max(1, ...usageEntries.map(([, v]) => v));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 min-[1180px]:grid-cols-5">
        <MiniStat label="Study · 7 days" help="study_minutes_7d" value={`${k.total_study_minutes_7d} min`}
          tone={baseline !== null && baseline > 0 && k.total_study_minutes_7d < Math.round(baseline * 7) ? 'down' : 'default'}
          sub={baseline !== null && baseline > 0 ? `usual ${Math.round(baseline * 7)}` : undefined} />
        <MiniStat label="Study · 30 days" help="study_minutes_30d" value={`${k.total_study_minutes_30d} min`}
          sub={classMinutes30d !== null ? `class ${Math.round(classMinutes30d)}` : undefined} />
        <MiniStat label="Per active day" help="avg_per_active_day" value={`${k.avg_minutes_per_active_day} min`}
          sub={series.length > 0 ? `${activeDays} active ${activeDays === 1 ? 'day' : 'days'}` : undefined} />
        <MiniStat label="Exams taken" help="All scored exam attempts by this student, ever." value={k.total_exams_taken} sub="all time" />
        <MiniStat label="Usual time" help="preferred_time" value={k.preferred_study_time ? k.preferred_study_time.charAt(0).toUpperCase() + k.preferred_study_time.slice(1) : '—'} />
      </div>

      <Box title="Daily study minutes" help="Minutes of study activity per day."
        subtitle={`Last 30 days${baseline !== null ? ` · dashed line is ${possessive(gender)} usual level (${Math.round(baseline)} min)` : ''}`}
        actions={series.length > 0 ? (
          <span className="flex flex-wrap gap-3.5 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-slate" />Studied</span>
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-accent" />No activity</span>
          </span>
        ) : undefined}>
        {series.length === 0 ? (
          <EmptyState compact icon={<FaChartLine />} title="No study activity yet" />
        ) : (
          <div role="img" aria-label="Daily study minutes over the last 30 days">
            <div className="relative">
              <ColumnBars buckets={buckets} height={CHART_H} variant="dense" minBar={4} />
              {baseline !== null && baseline > 0 && baseline <= chartMax && (
                <span aria-hidden="true" title={`Usual level ${Math.round(baseline)} min`}
                  className="pointer-events-none absolute inset-x-0 border-t-2 border-dashed border-accent-ink"
                  style={{ bottom: `${(baseline / chartMax) * CHART_H}px` }} />
              )}
            </div>
            <div className="mt-2.5 flex gap-1.5 font-mono text-[11px] font-medium text-muted">
              {axis.map((a) => <span key={a} className="flex-1 text-center">{a}</span>)}
            </div>
          </div>
        )}
      </Box>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {usageEntries.length > 0 && (
          <Box title="Feature usage" help="feature_usage" actions={<span className="text-xs text-muted">last 90 days · events</span>}>
            <ul>
              {usageEntries.map(([key, v]) => (
                <li key={key} className="grid grid-cols-[minmax(0,110px)_minmax(0,1fr)_34px] items-center gap-2.5 py-1.5 text-[13px]">
                  <span className="truncate text-ink">{humanize(key)}</span>
                  <Track value={(v / usageMax) * 100} label={humanize(key)} />
                  <span className="text-right font-mono text-ink">{v}</span>
                </li>
              ))}
            </ul>
          </Box>
        )}
        <Box title="AI usage" help="ai_usage">
          {data.ai_usage ? (
            <div className="grid grid-cols-2 gap-2.5">
              <MiniStat label="Tokens used" value={data.ai_usage.input_tokens_used + data.ai_usage.output_tokens_used} />
              <MiniStat label="Spend" value={`$${data.ai_usage.total_spent_usd.toFixed(2)}`} />
            </div>
          ) : (
            <EmptyState compact
              icon={<FaChartLine />}
              title="No AI features used yet"
              body="When this student uses AI flashcards, notes, or chat, usage shows up here."
            />
          )}
        </Box>
      </div>
    </div>
  );
}
